import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridPro, GridListViewColDef, GridRenderCellParams, Toolbar } from '@mui/x-data-grid-pro';
import { Box, Checkbox, Stack, Tooltip, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { csCZ, enUS, hrHR, huHU, itIT, roRO, skSK } from '@mui/x-data-grid/locales';

import { SearchComponent } from '@/components/common/Search';
import { ctxTachograph } from '@/contexts/TachographCtx';
import { ctxPopup } from '@/contexts/PopupContext';
import { TachographVehicleResponseDTO } from '@/generated/model';
import { getTachographVehicleData } from '@/generated/client/tachograph/tachograph';
import { date } from '@/utilities/dateUtils';
import TachographEmailButton from './TachographEmailButton';
import { TachoTabs } from './TachographPopup';
import { ToolbarSortItem } from '@/components/common/ToolbarSortItem';

export function LastDownloadedCell({ value }: { value?: number }) {
  const { t } = useTranslation();

  if (!value) {
    return <em>-{t('tachograph.table.noUploadYet')}-</em>;
  }

  const dateValue = new Date(value);
  const oneMonthAgo = new Date();
  oneMonthAgo.setDate(oneMonthAgo.getDate() - 31);

  const isOld = dateValue < oneMonthAgo;

  if (!isOld) {
    return <Box sx={{ marginLeft: '10px' }}>{date(dateValue, t)}</Box>;
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        borderRadius: '9999px',
        padding: '2px 10px',
        border: 1,
        borderColor: 'error.main',
        fontSize: '13px',
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        height: '28px',
        boxSizing: 'border-box',
      }}
    >
      <Tooltip title={t('tachograph.table.tooOld')} placement="top" arrow>
        <ErrorOutline sx={{ fontSize: 18, color: 'error.main' }} />
      </Tooltip>
      {date(dateValue, t)}
    </Box>
  );
}

export default function TachographVehicleData({
  isListView,
  activeTab,
}: {
  isListView: boolean;
  activeTab?: TachoTabs;
}) {
  const { t, i18n } = useTranslation();
  const tachographCtx = useContext(ctxTachograph);
  const popupCtx = useContext(ctxPopup);
  const [searchText, setSearchText] = useState<string | undefined>();
  const [filteredTachographData, setFilteredTachographData] = useState<TachographVehicleResponseDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [selectAll, setSelectAll] = useState(false);
  const dataRef = useRef<HTMLDivElement | null>(null);
  const pageSize = 50;

  const totalRows = filteredTachographData.length;
  const selectedCount = tachographCtx?.selectedRowIds.length ?? 0;
  const isIndeterminate = selectedCount > 0 && (selectedCount < totalRows || selectedCount === totalRows);

  useEffect(() => {
    if (!isListView) {
      dataRef.current!.style.width = `calc(100vw - ${popupCtx?.drawerMenuOpen ? 300 : 100}px`;
    }
  }, [popupCtx?.drawerMenuOpen]);

  const toVehicleRow = (row: TachographVehicleResponseDTO) => ({
    id: row.id,
    targetName: row.targetName,
    description: row.description,
    customer: row.customer,
    lastDownloadedTimeMillis: row.lastDownloadedTimeMillis,
  });

  const updateTachographData = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    async (pageT: number, pageSizeT: number, nowHasMore: boolean) => {
      if (!tachographCtx?.selectedDates || !nowHasMore || loading) return;
      setLoading(true);
      const request = {
        keyword: searchText,
        page: pageT,
        pageSize: pageSizeT,
        order: 'asc',
      };

      const response = await getTachographVehicleData(request);

      const formatted = response.data.map((row) => toVehicleRow(row));

      if (response.data.length < pageSize) setHasMore(false);
      setLoading(false);

      // TODO error handling
      setFilteredTachographData((prevRows) => [...prevRows, ...formatted]);
      setPage((prevPage) => prevPage + 1);
    },
    [searchText, tachographCtx?.selectedDates]
  );

  useEffect(() => {
    setFilteredTachographData([]);
    setHasMore(true);
    setPage(1);
    void updateTachographData(1, pageSize, true);
  }, [searchText, updateTachographData]);

  useEffect(() => {
    if (tachographCtx?.selectedRowIds.length === 0) {
      setSelectAll(false);
    }
  }, [tachographCtx?.selectedRowIds]);

  if (!tachographCtx) return;

  // FIXME a Localization-t nem tudja importalni :(
  const localeMap: Record<string, typeof enUS> = {
    en: enUS,
    hu: huHU,
    ro: roRO,
    sk: skSK,
    cz: csCZ,
    hr: hrHR,
    sr: enUS,
    sl: enUS,
    it: itIT,
  };

  function handleTemplateSelection(templateId: number) {
    tachographCtx?.setSelectedRowIds((prev) =>
      prev.includes(templateId) ? prev.filter((rowId) => rowId !== templateId) : [...prev, templateId]
    );
  }

  async function loadMoreRows() {
    await updateTachographData(page, pageSize, hasMore);
  }

  function handleSelectAllChange() {
    const selectedCount = tachographCtx?.selectedRowIds.length ?? 0;

    if (selectedCount > 0) {
      tachographCtx?.setSelectedRowIds([]);
      setSelectAll(false);
    } else {
      tachographCtx?.setSelectedRowIds(
        filteredTachographData.filter((row) => row.lastDownloadedTimeMillis !== null).map((row) => row.id)
      );
      setSelectAll(true);
    }
  }

  function ListViewCell(params: GridRenderCellParams<TachographVehicleResponseDTO>) {
    const lastDownload = params.row.lastDownloadedTimeMillis ?? 0;
    const isChecked = tachographCtx?.selectedRowIds.includes(params.row.id) && lastDownload > 0;

    return (
      <Stack
        direction="row"
        sx={{
          width: '100%',
          alignItems: 'center',
          height: '100%',
          gap: 2,
          paddingY: 1,
        }}
      >
        <Checkbox
          checked={isChecked}
          onChange={() => handleTemplateSelection(params.row.id)}
          className={isChecked ? 'tracking-body-target-list-item-selected' : ''}
          sx={{ marginLeft: '5px' }}
        />

        <Stack sx={{ flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={600} fontSize="14px">
            {params.row.targetName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {params.row.description}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {params.row.customer}
          </Typography>
        </Stack>

        <LastDownloadedCell value={params.row.lastDownloadedTimeMillis} />
      </Stack>
    );
  }

  const listViewColDef: GridListViewColDef<TachographVehicleResponseDTO> = {
    field: 'listView',
    renderCell: ListViewCell,
  };

  const columns = [
    {
      field: 'select',
      renderHeader: () => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={selectAll}
              disabled={loading}
              indeterminate={selectedCount > 0 && selectedCount < totalRows}
              onChange={handleSelectAllChange}
              className={
                selectAll || (selectedCount > 0 && selectedCount < totalRows)
                  ? 'tracking-body-target-list-item-selected'
                  : ''
              }
            />
            <span style={{ fontWeight: 600 }}>
              ({filteredTachographData.length}/{tachographCtx?.selectedRowIds.length})
            </span>
          </Box>
        );
      },
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isChecked =
          tachographCtx?.selectedRowIds.includes(params.row.id) && params.row.lastDownloadedTimeMillis > 0;
        return (
          <Checkbox
            checked={isChecked}
            onChange={() => handleTemplateSelection(params.row.id)}
            className={isChecked ? 'tracking-body-target-list-item-selected' : ''}
            sx={{ marginLeft: '10px' }}
          />
        );
      },
    },
    { field: 'targetName', headerName: t('tachograph.table.name'), flex: 1 },
    { field: 'description', headerName: t('tachograph.table.description'), flex: 1 },
    { field: 'customer', headerName: t('tachograph.table.customer'), flex: 1 },
    {
      field: 'lastDownloadedTimeMillis',
      headerName: t('tachograph.table.lastDownloadedData'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <LastDownloadedCell value={params.value} />,
    },
  ];

  function SelectAllAndOrderControl() {
    return (
      <div className="selector-toolbar">
        <span>
          <Checkbox
            checked={selectAll}
            disabled={loading}
            indeterminate={isIndeterminate}
            onChange={handleSelectAllChange}
            className={selectAll || isIndeterminate ? 'tracking-body-target-list-item-selected' : ''}
          />
          <span style={{ fontWeight: 600 }}>
            ({filteredTachographData.length}/{tachographCtx?.selectedRowIds.length})
          </span>
        </span>
        <ToolbarSortItem listView={isListView} />
      </div>
    );
  }

  function getColumnHeaders() {
    return {
      toolbar: () => (
        <Toolbar style={{ minHeight: isListView ? '120px' : '50px' }}>
          <div className="selector-header-container">
            <div className="report-search-field">
              <SearchComponent
                searchText={searchText}
                setSearchText={setSearchText}
                showHistory={false}
                placeholder={t('tachograph.filter.vehicleSearchPlaceholder')}
              />
            </div>
            {isListView && <SelectAllAndOrderControl />}
          </div>
        </Toolbar>
      ),
    };
  }

  return (
    <>
      <DataGridPro
        ref={dataRef}
        rows={filteredTachographData}
        columns={columns}
        disableRowSelectionOnClick
        listView={isListView}
        listViewColumn={listViewColDef}
        disableColumnMenu
        hideFooterRowCount
        hideFooter={isListView}
        onRowsScrollEnd={loadMoreRows}
        autosizeOptions={{
          columns: ['select', 'targetName', 'description', 'customer', 'timeMillis'],
        }}
        paginationMode="server"
        loading={loading}
        getRowHeight={() => 'auto'}
        getRowClassName={(params) => (params.row.lastDownloadedTimeMillis === null ? 'disabled-row' : '')}
        disableDensitySelector
        disableColumnFilter
        pageSizeOptions={[pageSize]}
        localeText={{
          ...localeMap[i18n.language].components.MuiDataGrid.defaultProps.localeText,
          noRowsLabel: t('tachograph.table.noData'),
        }}
        showToolbar
        slots={getColumnHeaders()}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-footerContainer': {
            boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.1)',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-toolbar': {
            borderBottom: 'none',
            boxShadow: 'none',
          },
        }}
      />

      {isListView && tachographCtx.selectedRowIds.length > 0 && <TachographEmailButton activeTab={activeTab} />}
    </>
  );
}
